import { setStorageItem, getStorageItem, removeStorageItem } from '@parrot-software/utils';
import type { IReportStoragePort } from '@reports/application/ports';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import { REPORT_STORAGE_KEYS } from './types';
import type { FiltersMetadata } from './types';

/**
 * Serialized version of BlockDataRequestDto for localStorage
 * Dates are stored as ISO strings and converted back to Date objects when loaded
 */
type SerializedBlockDataRequestDto = Omit<BlockDataRequestDto, 'startDate' | 'endDate'> & {
  startDate: string;
  endDate: string;
};

/**
 * Creates a repository for Report Storage operations
 *
 * Implements IReportStoragePort using localStorage for persistence.
 * Handles saving, retrieving, and clearing applied filters.
 * Automatically serializes/deserializes Date objects to/from ISO strings.
 *
 * Note: localStorage operations are synchronous, but the interface returns Promises
 * to support future async storage implementations (IndexedDB, remote storage, etc.)
 *
 * @returns ReportStorageRepository instance implementing IReportStoragePort
 *
 * @example
 * const storageRepository = createReportStorageRepository();
 *
 * // Save filters (Date objects are automatically serialized)
 * await storageRepository.saveAppliedFilters({
 *   startDate: new Date('2025-01-01'),
 *   endDate: new Date('2025-01-31'),
 *   filters: { brands: ['brand1'] }
 * });
 *
 * // Get filters (Date strings are automatically deserialized to Date objects)
 * const filters = await storageRepository.getAppliedFilters();
 *
 * // Clear filters
 * await storageRepository.clearAppliedFilters();
 */
export const createReportStorageRepository = (): IReportStoragePort => {
  /**
   * Serializes BlockDataRequestDto for storage
   * Converts Date objects to ISO strings
   */
  const serializeFilters = (filters: BlockDataRequestDto): SerializedBlockDataRequestDto => {
    return {
      ...filters,
      startDate: filters.startDate.toISOString(),
      endDate: filters.endDate.toISOString(),
    };
  };

  /**
   * Deserializes stored filters back to BlockDataRequestDto
   * Converts ISO strings back to Date objects
   */
  const deserializeFilters = (serialized: SerializedBlockDataRequestDto): BlockDataRequestDto => {
    return {
      ...serialized,
      startDate: new Date(serialized.startDate),
      endDate: new Date(serialized.endDate),
    };
  };

  /**
   * Saves the applied filters to localStorage
   * Automatically serializes Date objects to ISO strings
   *
   * @param filters - The filters to save
   * @returns Promise that resolves when the filters are saved
   */
  const saveAppliedFilters = async (filters: BlockDataRequestDto): Promise<void> => {
    const serialized = serializeFilters(filters);
    setStorageItem(REPORT_STORAGE_KEYS.APPLIED_FILTERS, serialized);
  };

  /**
   * Gets the applied filters from localStorage
   * Automatically deserializes ISO strings back to Date objects
   *
   * @returns Promise that resolves with the applied filters or null if not found
   */
  const getAppliedFilters = async (): Promise<BlockDataRequestDto | null> => {
    const result = getStorageItem<SerializedBlockDataRequestDto | null>(REPORT_STORAGE_KEYS.APPLIED_FILTERS, null);

    if (!result.success || !result.data) {
      return null;
    }

    try {
      return deserializeFilters(result.data);
    } catch {
      // If deserialization fails (e.g., invalid date strings), return null
      return null;
    }
  };

  /**
   * Clears the applied filters from localStorage
   *
   * @returns Promise that resolves when the filters are cleared
   */
  const clearAppliedFilters = async (): Promise<void> => {
    removeStorageItem(REPORT_STORAGE_KEYS.APPLIED_FILTERS);
  };

  /**
   * Gets the filters metadata from localStorage
   * Metadata tracks store UUID and report path to detect context changes
   *
   * @returns Promise that resolves with the filters metadata or null if not found
   */
  const getFiltersMetadata = async (): Promise<FiltersMetadata | null> => {
    const result = getStorageItem<FiltersMetadata | null>(REPORT_STORAGE_KEYS.FILTERS_METADATA, null);

    if (!result.success || !result.data) {
      return null;
    }

    return result.data;
  };

  /**
   * Saves the filters metadata to localStorage
   * Metadata tracks store UUID and report path to detect context changes
   *
   * @param metadata - The metadata to save
   * @returns Promise that resolves when the metadata is saved
   */
  const saveFiltersMetadata = async (metadata: FiltersMetadata): Promise<void> => {
    setStorageItem(REPORT_STORAGE_KEYS.FILTERS_METADATA, metadata);
  };

  /**
   * Clears the filters metadata from localStorage
   *
   * @returns Promise that resolves when the metadata is cleared
   */
  const clearFiltersMetadata = async (): Promise<void> => {
    removeStorageItem(REPORT_STORAGE_KEYS.FILTERS_METADATA);
  };

  return {
    saveAppliedFilters,
    getAppliedFilters,
    clearAppliedFilters,
    getFiltersMetadata,
    saveFiltersMetadata,
    clearFiltersMetadata,
  };
};
